import {
  useCompleteConnectorOAuth,
  useConnectConnector,
} from "@/api/connectors";
import { usePostPipedreamConnectUserToken } from "@/api/integrations/pipedream";
import type { MCPOAuthMessage } from "@/hooks/useConnectMCP";
import analytics from "@/utils/analytics";
import { Connector } from "@cohesive/api-client";
import { createFrontendClient } from "@pipedream/sdk/browser";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export type RunConnectVariables = {
  connector: Connector;
  inputs: Record<string, string>;
  fields: Record<string, string>;
};

/**
 * "connected-in-popup" when the popup lost its opener and completed the connect itself, recording
 * it; "cancelled" when the user closed the provider's window before finishing; "redirected" when
 * the tab is leaving for the provider and the landing page completes the connect.
 */
export type RunConnectOutcome =
  | "connected"
  | "connected-in-popup"
  | "cancelled"
  | "redirected";

export const RUN_CONNECT_MUTATION_KEY = ["run-connect"];

/** Where a popup that completed a connect itself tells the dashboard's other windows. */
export const CONNECTOR_OAUTH_CHANNEL = "connector-oauth";

export type ConnectorOAuthCompletedMessage = {
  type: "connector-oauth-completed";
  connectorId: string;
};

type ConnectorOAuthCallbackMessage = {
  type: "connector-oauth-callback";
  code: string;
  state: string;
};

/** Connects a connector; `run` must be called from the click so an OAuth popup may open. */
export function useRunConnect() {
  const queryClient = useQueryClient();
  const { mutateAsync: connect } = useConnectConnector();
  const { mutateAsync: completeOAuth } = useCompleteConnectorOAuth();
  const { mutateAsync: createPipedreamToken } =
    usePostPipedreamConnectUserToken();

  const mutation = useMutation<
    RunConnectOutcome,
    Error,
    RunConnectVariables & { popup: Window | null }
  >({
    mutationKey: RUN_CONNECT_MUTATION_KEY,
    mutationFn: async ({ connector, inputs, fields, popup }) => {
      switch (connector.auth_method) {
        case "oauth": {
          let authorizationURL: string;
          try {
            const { authorization_url } = await connect({
              connectorId: connector.id,
              inputs,
              popup: !!popup,
            });
            if (!authorization_url) {
              throw new Error("No authorization URL received from server");
            }
            authorizationURL = authorization_url;
          } catch (error) {
            popup?.close();
            throw error;
          }
          if (!popup) {
            window.location.assign(authorizationURL);
            return "redirected";
          }
          popup.location.href = authorizationURL;
          const callback = await authorizeInPopup(popup);
          if (callback) {
            await completeOAuth({ connector, ...callback });
            return "connected";
          }
          // A popup that lost its opener lands on the dashboard and completes the connect there.
          // Losing it to a COOP page reads as closed at once, so a later completion arrives on
          // CONNECTOR_OAUTH_CHANNEL instead.
          await queryClient.invalidateQueries({ queryKey: ["connectors"] });
          const connection = queryClient
            .getQueryData<Connector[]>(["connectors"])
            ?.find((c) => c.id === connector.id)?.connection;
          return connection?.status === "enabled" &&
            connection.id !== connector.connection?.id
            ? "connected-in-popup"
            : "cancelled";
        }
        case "pipedream": {
          const response = await createPipedreamToken();
          const accountId = await connectPipedreamAccount(
            connector,
            response.data.data.token,
          );
          if (!accountId) return "cancelled";
          await connect({
            connectorId: connector.id,
            inputs,
            account_id: accountId,
          });
          return "connected";
        }
        default:
          await connect({ connectorId: connector.id, inputs, fields });
          return "connected";
      }
    },
    // An OAuth completion records itself.
    onSuccess: (outcome, { connector }) => {
      if (outcome !== "connected" || connector.auth_method === "oauth") return;
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      analytics.trackConnectorConnected({
        connectorId: connector.id,
        slug: connector.slug,
        authMethod: connector.auth_method,
        reconnect: !!connector.connection,
      });
    },
  });

  return {
    isPending: mutation.isPending,
    connector: mutation.isPending ? mutation.variables?.connector : undefined,
    run: (variables: RunConnectVariables) =>
      mutation.mutateAsync({
        ...variables,
        // Opened before any request so the browser counts it as opened by the click.
        popup:
          variables.connector.auth_method === "oauth"
            ? window.open(
                "",
                "connector-oauth",
                "width=600,height=700,scrollbars=yes",
              )
            : null,
      }),
  };
}

/** Resolves with what the callback handed back, or null when the user closed the window. */
function authorizeInPopup(
  popup: Window,
): Promise<Omit<ConnectorOAuthCallbackMessage, "type"> | null> {
  return new Promise((resolve, reject) => {
    const finish = () => {
      window.removeEventListener("message", onMessage);
      clearInterval(timer);
      popup.close();
    };
    // The callback page is served by the API, so its origin is not ours; the window is the check.
    const onMessage = (
      event: MessageEvent<ConnectorOAuthCallbackMessage | MCPOAuthMessage>,
    ) => {
      if (event.source !== popup) return;
      if (event.data?.type === "connector-oauth-callback") {
        finish();
        resolve({ code: event.data.code, state: event.data.state });
      } else if (event.data?.type === "mcp-oauth-error") {
        finish();
        reject(new Error(event.data.error || "Authorization failed"));
      }
    };
    window.addEventListener("message", onMessage);

    const timer = setInterval(() => {
      if (popup.closed) {
        finish();
        resolve(null);
      }
    }, 500);
  });
}

/** Resolves with the connected account's id, or null when the user closed the iframe. */
function connectPipedreamAccount(
  connector: Connector,
  token: string,
): Promise<string | null> {
  const pd = createFrontendClient();
  return new Promise((resolve, reject) => {
    pd.connectAccount({
      app: connector.provider_id ?? "",
      token,
      oauthAppId: connector.pipedream_oauth_app_id,
      onSuccess: (account) => {
        // @ts-expect-error - cleanup is a private method but we need to call it to close the iframe
        pd.cleanup();
        resolve(account.id);
      },
      onError: (err) =>
        reject(new Error(`Pipedream connection failed: ${err.message}`)),
      onClose: (status) => {
        if (!status.completed) resolve(null);
      },
    });
  });
}
